import { httpClient } from "./http-clients/http-client";

export namespace MediaPreloader {
    /**
     * Preloads an image asynchronously.
     *
     * @param {string} imageSrc - The URL of the image to preload.
     * @return {Promise<void>} A promise that resolves when the image is loaded successfully, or rejects if there is an error.
     */
    export function preloadImage(imageSrc: string): Promise<void> {
        return new Promise((resolve, reject) => {
            const image = new Image();
            image.onload = () => resolve();
            image.onerror = () => {
                console.error(`Failed to load image ${imageSrc}.`);
                reject(new Error(`Failed to load image ${imageSrc}.`));
            };
            image.src = imageSrc;
        });
    }

    /**
     * Preloads an array of images asynchronously.
     *
     * @param {string[]} imagesSrc - An array of image URLs to preload.
     * @return {Promise<number>} A promise that resolves with the number of images successfully loaded.
     */
    export async function preloadImages(imagesSrc: string[]): Promise<number> {
        return (await Promise.all(imagesSrc.map((imageSrc) => preloadImage(imageSrc)))).length;
    }

    /**
     * Asynchronously preloads a video from the provided URL and returns a URL object
     * representing the preloaded video.
     *
     * @param {string} videoUrl - The URL of the video to preload.
     * @return {Promise<string>} A Promise that resolves to a URL object representing the
     * preloaded video.
     */
    export async function preloadVideo(videoUrl: string): Promise<string> {
        const data = await httpClient.get(videoUrl, {
            responseType: "blob",
        });
        return URL.createObjectURL(data.data);
    }

    /**
     * Preloads an array of videos asynchronously.
     *
     * @param {string[]} videoUrls - An array of video URLs to preload.
     * @return {Promise<number>} A promise that resolves with the number of videos successfully loaded.
     */
    export async function preloadVideos(videoUrls: string[]): Promise<number> {
        return (await Promise.all(videoUrls.map((videoSrc) => preloadVideo(videoSrc)))).length;
    }
}
